import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useCallback, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTransactionContext } from "../../../../contexts/TransactionContext";
import useTransactionFormType from "../../../../hooks/ui-states/useTransactionFormType";
import { WalletWithAllocateAmount } from "../components/LendTransactionForm";
import { Category, Wallet } from "../components/types";
import { mapUrlTabToEnum } from "../helpers";

export const useTransactionFormState = () => {
  const location = useLocation();
  const { newTransactionCount, setNewTransactionCount } = useTransactionContext();
  const [transaction] = useState<Transaction | null>(location?.state?.transaction);
  const [firstRender, setFirstRender] = useState(location?.state?.firstRender || true);
  const [currentTransactionType, setCurrentTransactionType] = useTransactionFormType();
  const currentTab = mapUrlTabToEnum(currentTransactionType);

  // Dialog states
  const [openTransactionTypeSelector, setOpenTransactionTypeSelector] = useState(false);
  const [openDialogSelectedExpenseCategory, setOpenDialogSelectedExpenseCategory] = useState(false);
  const [openDialogSelectedIncomeCategory, setOpenDialogSelectedIncomeCategory] = useState(false);
  const [openDialogSelectWallets, setOpenDialogSelectWallets] = useState(false);
  const [openDialogSelectDestinationWallets, setOpenDialogSelectDestinationWallets] = useState(false);
  const [openDatePicker, setOpenDatePicker] = useState(false);

  // Form data states
  const [spendingCategory, setSpendingCategory] = useState<Category | null>(null);
  const [incomeCategory, setIncomeCategory] = useState<Category | null>(null);
  const [expenseWallet, setExpenseWallet] = useState<Wallet | null>(null);
  const [incomeWallet, setIncomeWallet] = useState<Wallet | null>(null);
  const [transferSourceWallet, setTransferSourceWallet] = useState<Wallet | null>(null);
  const [selectDestinationWallet, setSelectDestinationWallet] = useState<Wallet | null>(null);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState<number | null>(Math.abs(transaction?.amount || 0) || null);
  const [datePicker, setDatePicker] = useState<Dayjs | any>(dayjs());

  // Installment states
  const [isInstallment, setIsInstallment] = useState(false);
  const [numberOfInstallments, setNumberOfInstallments] = useState<number | null>(null);
  const [installmentFee, setInstallmentFee] = useState<number | null>(null);

  // Loan states
  const [loanWallet, setLoanWallet] = useState<Wallet | null>(null);
  const [lendWallets, setLendWallets] = useState<WalletWithAllocateAmount[] | null>([]);
  const [lendExpendWallet, setLendExpenseWallet] = useState<WalletWithAllocateAmount | null>(null);
  const [repaymentWallet, setRepaymentWallet] = useState<Wallet | null>(null);
  const [repaymentDate, setRepaymentDate] = useState<Dayjs>();
  const [sourceWallet, setSourceWallet] = useState<Wallet | null>(null);
  const [loanType, setLoanType] = useState<string | null>('borrow');
  const [creditCardType, setCreditCardType] = useState<string | null>(null);
  const [loansOrdering, setLoansOrdering] = useState<{ loanId: string, orderIndex: number }[]>([]);
  const [remainingAmount, setRemainingAmount] = useState<number | undefined>(undefined);
  const [initialLoanWallet, setInitialLoanWallet] = useState<any>(null);
  const [initialDebtWallet, setInitialDebtWallet] = useState<any>(null);

  // Transaction type confirmation - default to true to show expense form by default
  const [transactionTypeConfirmed, setTransactionTypeConfirmed] = useState(true);
  const transactionTypeConfirmedRef = useRef(true);

  // Flag to track when wallets are being reset (to prevent auto-open dialogs)
  const isResettingWalletsRef = useRef(false);
  // Counter to trigger re-render after wallet reset is complete
  const [walletResetCounter, setWalletResetCounter] = useState(0);

  // Flag to track when user is manually opening Transaction Type selector
  // This is set on mousedown (before useClickOutside fires) to prevent selectorCallback from opening other dialogs
  const isManuallyOpeningTypeSelectorRef = useRef(false);

  // Validation states
  const [amountError, setAmountError] = useState<string | null>(null);
  const [isExcludedFromReport, setIsExcludedFromReport] = useState<boolean>(transaction?.isExcludedFromReport || false);

  // Refs
  const amountInputRef = useRef<HTMLInputElement>(null);
  const descriptionInputRef = useRef<HTMLInputElement>(null);
  const installmentTermInputRef = useRef<HTMLInputElement>(null);

  const isUpdate = !!transaction?.id;

  const handleOnCloseDialog = useCallback(() => {
    setOpenDialogSelectedExpenseCategory(false);
    setOpenDialogSelectedIncomeCategory(false);
    setOpenDialogSelectWallets(false);
    setOpenDialogSelectDestinationWallets(false);
    setOpenDatePicker(false);
  }, []);

  const resetForm = useCallback(() => {
    setAmount(null);
    setDescription('');
    setDatePicker(dayjs());
    setRepaymentDate(undefined);
    setIsInstallment(false);
  }, []);

  const resetWallets = useCallback(() => {
    isResettingWalletsRef.current = true;
    setExpenseWallet(null);
    setIncomeWallet(null);
    setTransferSourceWallet(null);
    setSelectDestinationWallet(null);
    setLoanWallet(null);
    setLendWallets([]);
    setLendExpenseWallet(null);
    setRepaymentWallet(null);
    setSourceWallet(null);
    setSpendingCategory(null);
    setIncomeCategory(null);
    setLoansOrdering([]);
    setRemainingAmount(undefined);
  }, []);

  const clearResettingWalletsFlag = useCallback(() => {
    isResettingWalletsRef.current = false;
    // Increment counter to trigger the useEffect to re-run
    setWalletResetCounter(c => c + 1);
  }, []);

  return {
    // Location and context
    location,
    transaction,
    isUpdate,
    newTransactionCount,
    setNewTransactionCount,

    // Transaction type
    currentTransactionType,
    setCurrentTransactionType,
    currentTab,
    transactionTypeConfirmed,
    setTransactionTypeConfirmed,
    transactionTypeConfirmedRef,
    loanType,
    setLoanType,
    creditCardType,
    setCreditCardType,

    // First render
    firstRender,
    setFirstRender,

    // Dialog states
    openTransactionTypeSelector,
    setOpenTransactionTypeSelector,
    openDialogSelectedExpenseCategory,
    setOpenDialogSelectedExpenseCategory,
    openDialogSelectedIncomeCategory,
    setOpenDialogSelectedIncomeCategory,
    openDialogSelectWallets,
    setOpenDialogSelectWallets,
    openDialogSelectDestinationWallets,
    setOpenDialogSelectDestinationWallets,
    openDatePicker,
    setOpenDatePicker,

    // Categories
    spendingCategory,
    setSpendingCategory,
    incomeCategory,
    setIncomeCategory,

    // Wallets
    expenseWallet,
    setExpenseWallet,
    incomeWallet,
    setIncomeWallet,
    transferSourceWallet,
    setTransferSourceWallet,
    selectDestinationWallet,
    setSelectDestinationWallet,

    // Form data
    description,
    setDescription,
    amount,
    setAmount,
    datePicker,
    setDatePicker,

    // Installment
    isInstallment,
    setIsInstallment,
    numberOfInstallments,
    setNumberOfInstallments,
    installmentFee,
    setInstallmentFee,

    // Loan
    loanWallet,
    setLoanWallet,
    lendWallets,
    setLendWallets,
    lendExpendWallet,
    setLendExpenseWallet,
    repaymentWallet,
    setRepaymentWallet,
    repaymentDate,
    setRepaymentDate,
    sourceWallet,
    setSourceWallet,
    loansOrdering,
    setLoansOrdering,
    remainingAmount,
    setRemainingAmount,
    initialLoanWallet,
    setInitialLoanWallet,
    initialDebtWallet,
    setInitialDebtWallet,

    // Validation
    amountError,
    setAmountError,
    isExcludedFromReport,
    setIsExcludedFromReport,

    // Refs
    amountInputRef,
    descriptionInputRef,
    installmentTermInputRef,

    // Handlers
    handleOnCloseDialog,
    resetForm,
    resetWallets,
    isResettingWalletsRef,
    clearResettingWalletsFlag,
    walletResetCounter,
    isManuallyOpeningTypeSelectorRef,
  };
};

export type TransactionFormState = ReturnType<typeof useTransactionFormState>;
